import { createContext, useCallback, useContext, useRef, useState } from "react";
import { toast } from "sonner";

/**
 * Global playback state: mute, volume, and which single video may play.
 *
 * Lives in its own module rather than in App.js because PlayerContext and the
 * video page both need it, and importing it from App.js would form a cycle -
 * App.js renders PlayerProvider, so PlayerProvider importing App.js back has
 * already crashed this project once with a provider stuck in its temporal dead
 * zone.
 */
const AudioContext = createContext();

export const useAudio = () => useContext(AudioContext);

export const AudioProvider = ({ children }) => {
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(80);
  const [activeVideoId, setActiveVideoId] = useState(null);
  const previousActiveVideoRef = useRef(null);

  /**
   * Feed cards enforce one-at-a-time among themselves through activeVideoId, but
   * the modal and the video page are separate players that knew nothing about it -
   * so opening either left the card underneath still playing and two soundtracks
   * ran at once. A full-screen player claims exclusivity while it is mounted and
   * the feed goes quiet.
   *
   * Counted rather than a boolean: a modal opened from a video page holds two
   * claims, and closing only the modal must not hand playback back to the feed.
   */
  const [exclusiveClaims, setExclusiveClaims] = useState(0);

  /** Claim playback; call the returned function to release it. */
  const claimExclusivePlayback = useCallback(() => {
    setExclusiveClaims((n) => n + 1);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      setExclusiveClaims((n) => Math.max(0, n - 1));
    };
  }, []);

  const toggleMute = () => {
    setIsMuted((prev) => !prev);
    toast.success(isMuted ? "Sound ON" : "Sound OFF");
  };

  const updateVolume = (newVolume) => {
    setVolume(Math.max(0, Math.min(100, newVolume)));
    if (newVolume === 0) {
      setIsMuted(true);
    } else if (isMuted && newVolume > 0) {
      setIsMuted(false);
    }
  };

  // Only one feed card plays at a time.
  const handleSetActiveVideoId = useCallback(
    (newVideoId) => {
      previousActiveVideoRef.current = activeVideoId;
      setActiveVideoId(newVideoId);
    },
    [activeVideoId]
  );

  return (
    <AudioContext.Provider
      value={{
        isMuted,
        setIsMuted,
        toggleMute,
        volume,
        setVolume: updateVolume,
        activeVideoId,
        setActiveVideoId: handleSetActiveVideoId,
        previousActiveVideoId: previousActiveVideoRef.current,
        // False while a modal or video page owns playback.
        feedAutoplayAllowed: exclusiveClaims === 0,
        claimExclusivePlayback,
      }}
    >
      {children}
    </AudioContext.Provider>
  );
};
